import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  ErrorState,
  NoPermissionState,
  Notice,
  PageHeader,
  Skeleton,
  Toolbar,
} from '@horevia/ui';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Ban, Coins, History, Wallet } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from '@/features/auth/session';
import { CancelExpenseDialog } from '@/features/expenses/CancelExpenseDialog';
import { expenseQueryOptions } from '@/features/expenses/queries';
import { expenseActions, expenseMethodTone, expenseStatusTone } from '@/features/expenses/status';
import { cashSessionQueryOptions } from '@/features/payments/queries';
import { errorMessage, errorTraceId } from '@/lib/errors';
import { formatAmount, formatDateShort, formatDateTime } from '@/lib/format';

export const Route = createFileRoute('/_app/finance/expenses/$expenseId')({
  component: ExpenseDetailPage,
});

/**
 * Fiche dépense (D-052) : détail, lien vers la session de caisse pour une
 * dépense en espèces, annulation avec motif (`expense.cancel`, verrou
 * optimiste). Une dépense annulée reste consultable, barrée.
 */
function ExpenseDetailPage() {
  const { expenseId } = Route.useParams();
  const { t, i18n } = useTranslation(['expenses', 'cash', 'common', 'errors']);
  const session = useSession();
  const canRead = session.can('expense.read');
  const [cancelOpen, setCancelOpen] = useState(false);

  const query = useQuery(expenseQueryOptions(expenseId, canRead));
  const expense = query.data;
  const cashSession = useQuery(
    cashSessionQueryOptions(
      expense?.cashSessionId ?? '',
      Boolean(expense?.cashSessionId) && session.can('cash.read'),
    ),
  );

  if (!canRead) {
    return (
      <Container>
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionDescription')}
        />
      </Container>
    );
  }

  if (query.isPending) {
    return (
      <Container>
        <div className="stack" aria-busy="true">
          <Skeleton variant="title" />
          <Skeleton height={80} />
          <Skeleton height={300} />
        </div>
      </Container>
    );
  }

  if (query.isError || !expense) {
    return (
      <Container>
        <ErrorState
          title={t('common:states.errorTitle')}
          description={errorMessage(query.error, t)}
          traceId={errorTraceId(query.error)}
          retryLabel={t('common:actions.retry')}
          onRetry={() => void query.refetch()}
          traceCopyLabel={t('common:actions.copy')}
          traceCopiedLabel={t('common:actions.copied')}
        />
      </Container>
    );
  }

  const actions = expenseActions(expense, session.can);
  const cancelled = expense.status === 'CANCELLED';
  const cashMovement = cashSession.data?.movements.find(
    (movement) => movement.id === expense.cashMovementId,
  );

  return (
    <Container>
      <PageHeader
        title={expense.reference}
        subtitle={t('expenses:detail.subtitle', {
          category: t(`expenses:category.${expense.category}`),
          date: formatDateShort(`${expense.expenseDate}T00:00:00.000Z`, i18n.language),
        })}
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          {
            label: t('expenses:title'),
            link: <Link to="/finance/expenses">{t('expenses:title')}</Link>,
          },
          { label: expense.reference },
        ]}
        actions={
          <div className="row row--wrap" style={{ gap: 8 }}>
            <Badge tone={expenseStatusTone(expense.status)} size="lg" dot>
              {t(`expenses:status.${expense.status}`)}
            </Badge>
            <Badge tone={expenseMethodTone(expense.method, expense.provider)} size="lg">
              {expense.provider
                ? t(`expenses:provider.${expense.provider}`)
                : t(`expenses:method.${expense.method}`)}
            </Badge>
          </div>
        }
      />

      <Card className="mb-4">
        <Toolbar style={{ borderBottom: 0, borderRadius: 'var(--r-md)' }}>
          <span
            className="strong display t-16"
            style={{ textDecoration: cancelled ? 'line-through' : undefined }}
          >
            {formatAmount(expense.amount, i18n.language, expense.currencyCode)}
          </span>
          <span className="spacer" />
          {actions.openCashSession && expense.cashSessionId ? (
            <Button variant="secondary" size="sm" asChild>
              <Link to="/billing/cash/$sessionId" params={{ sessionId: expense.cashSessionId }}>
                <Wallet className="ico ico-14" aria-hidden="true" />
                {t('expenses:actions.viewCashSession')}
              </Link>
            </Button>
          ) : null}
          {actions.cancel ? (
            <Button variant="danger" size="sm" onClick={() => setCancelOpen(true)}>
              <Ban className="ico ico-14" aria-hidden="true" />
              {t('expenses:actions.cancel')}
            </Button>
          ) : null}
        </Toolbar>
      </Card>

      {cancelled ? (
        <Notice
          tone="danger"
          className="mb-4"
          title={t('expenses:detail.cancelledTitle')}
          icon={<Ban className="ico ico-20 notice__ico" aria-hidden="true" />}
        >
          {t('expenses:detail.cancelledBody', {
            reason: expense.cancellationReason ?? '—',
            author: expense.cancelledBy?.fullName ?? '—',
            date: expense.cancelledAt ? formatDateTime(expense.cancelledAt, i18n.language) : '—',
          })}
        </Notice>
      ) : null}

      <div className="grid grid--main-aside">
        <div className="stack">
          <Card>
            <CardHeader
              title={t('expenses:detail.summaryTitle')}
              icon={<Coins className="ico ico-16" aria-hidden="true" />}
              iconTone="amber"
            />
            <CardBody density="tight">
              <dl className="dl dl--lines t-13">
                <dt>{t('expenses:columns.date')}</dt>
                <dd>{formatDateShort(`${expense.expenseDate}T00:00:00.000Z`, i18n.language)}</dd>
                <dt>{t('expenses:columns.amount')}</dt>
                <dd>
                  <b>{formatAmount(expense.amount, i18n.language, expense.currencyCode)}</b>
                </dd>
                <dt>{t('expenses:columns.method')}</dt>
                <dd>
                  {t(`expenses:method.${expense.method}`)}
                  {expense.provider ? ` · ${t(`expenses:provider.${expense.provider}`)}` : ''}
                </dd>
                {expense.transactionReference ? (
                  <>
                    <dt>
                      {expense.method === 'CHEQUE'
                        ? t('expenses:fields.chequeNumber')
                        : t('expenses:detail.transactionReference')}
                    </dt>
                    <dd className="mono">{expense.transactionReference}</dd>
                  </>
                ) : null}
                <dt>{t('expenses:columns.category')}</dt>
                <dd>{t(`expenses:category.${expense.category}`)}</dd>
                <dt>{t('expenses:columns.beneficiary')}</dt>
                <dd>{expense.beneficiary ?? t('expenses:detail.noBeneficiary')}</dd>
                <dt>{t('expenses:fields.description')}</dt>
                <dd>{expense.description}</dd>
              </dl>
            </CardBody>
          </Card>

          {expense.method === 'CASH' ? (
            <Card>
              <CardHeader
                title={t('expenses:detail.cashTitle')}
                icon={<Wallet className="ico ico-16" aria-hidden="true" />}
                iconTone="green"
                actions={
                  actions.openCashSession && expense.cashSessionId ? (
                    <Button variant="ghost" size="sm" asChild>
                      <Link
                        to="/billing/cash/$sessionId"
                        params={{ sessionId: expense.cashSessionId }}
                      >
                        {t('expenses:actions.viewCashSession')}
                      </Link>
                    </Button>
                  ) : null
                }
              />
              <CardBody density="tight">
                <p className="t-13 mb-0">
                  {cashSession.data
                    ? t('expenses:detail.cashBody', {
                        date: formatDateTime(cashSession.data.session.openedAt, i18n.language),
                      })
                    : t('expenses:detail.cashBodyNoSession')}
                </p>
                {cashMovement ? (
                  <p className="t-12 muted mb-0 mt-2">
                    {formatDateTime(cashMovement.occurredAt, i18n.language)} ·{' '}
                    {cashMovement.description} · −{formatAmount(cashMovement.amount, i18n.language)}
                  </p>
                ) : null}
              </CardBody>
            </Card>
          ) : null}
        </div>

        <aside className="stack sticky-aside">
          <Card>
            <CardHeader
              title={t('expenses:detail.historyTitle')}
              icon={<History className="ico ico-16" aria-hidden="true" />}
              iconTone="blue"
            />
            <CardBody>
              <div className="timeline">
                <div className="tl-item">
                  <span className="tl-item__dot">
                    <Coins className="ico" aria-hidden="true" />
                  </span>
                  <div className="tl-item__title">{t('expenses:detail.eventRecorded')}</div>
                  <div className="tl-item__meta">
                    {formatDateTime(expense.createdAt, i18n.language)} ·{' '}
                    {expense.recordedBy.fullName}
                  </div>
                </div>
                {cancelled ? (
                  <div className="tl-item">
                    <span className="tl-item__dot">
                      <Ban className="ico" aria-hidden="true" />
                    </span>
                    <div className="tl-item__title">{t('expenses:detail.eventCancelled')}</div>
                    <div className="tl-item__meta">
                      {expense.cancelledAt
                        ? formatDateTime(expense.cancelledAt, i18n.language)
                        : '—'}{' '}
                      · {expense.cancelledBy?.fullName ?? '—'} · {expense.cancellationReason}
                    </div>
                  </div>
                ) : null}
              </div>
            </CardBody>
          </Card>
        </aside>
      </div>

      <CancelExpenseDialog open={cancelOpen} onOpenChange={setCancelOpen} expense={expense} />
    </Container>
  );
}
